import { useSelector } from "react-redux";

import { ICheckedForecast } from "store/features";
import { RootState } from "store/rootReducer";
import {
  selectCheckedGlobalTypeWells,
  selectCheckedTypeWellKeys,
  selectCheckedTypeWells,
  selectSelectedTypeWell,
  useForecastStore
} from "stores/forecast";

import { UserArpsItem } from "models/UserArpsModel";

import "../context/reducer/arpsReducer";
import useForecastFolderUpdater from "./useTypeWellFolderUpdater";
import { Command, IUndoRedoManager } from "./useUndoRedo";

export default function useTypeWellUpdater(undoRedoManager: IUndoRedoManager) {
  const checkedForecasts = useForecastStore(selectCheckedTypeWells);
  const filteredTypeWellFolders = useSelector(
    (state: RootState) => state.arps.filteredTypeWellFolders
  );

  const checkedTypeWells = checkedForecasts
    .map((forecast) =>
      filteredTypeWellFolders
        .flatMap((folder) => folder.children)
        .find((child) => child.id === forecast.id)
    )
    .filter(Boolean) as ICheckedForecast[];

  const { updateTypeWellDataWithParentNode } = useForecastFolderUpdater();

  const selectedTypeWell = useForecastStore(selectSelectedTypeWell);
  const checkedTypeWellKeys = useForecastStore(selectCheckedTypeWellKeys);
  const checkedGlobalTypeWells = useForecastStore(selectCheckedGlobalTypeWells);
  const setCheckedGlobalTypeWells = useForecastStore((s) => s.setCheckedGlobalTypeWells);
  const setHasUnsavedChanges = useForecastStore((s) => s.setHasUnsavedChanges);
  const setSelectedTypeWell = useForecastStore((s) => s.setSelectedTypeWell);
  const setCheckedTypeWells = useForecastStore((s) => s.setCheckedTypeWells);
  const setCheckedTypeWellKeys = useForecastStore((s) => s.setCheckedTypeWellKeys);
  const setIsTypeWellSavable = useForecastStore((s) => s.setIsTypeWellSavable);

  const changeSelectedTypeWell = (typeWellToBeSelected: UserArpsItem) => {
    // Update well data of selected well without erasing in progress changes of selectedTypeWell.
    if (selectedTypeWell?.id !== typeWellToBeSelected?.id) {
      setSelectedTypeWell(typeWellToBeSelected);
      // Clear unsaved changes flag when switching to a different type well
      setHasUnsavedChanges(false);
    } else if (selectedTypeWell?.wellData !== typeWellToBeSelected.wellData) {
      const selectedTypeWellWithUpdatedWellData = {
        ...selectedTypeWell,
        wellData: typeWellToBeSelected?.wellData
      };
      setSelectedTypeWell(selectedTypeWellWithUpdatedWellData);
    } else {
      setSelectedTypeWell(typeWellToBeSelected);
    }

    setIsTypeWellSavable(true);

    if (
      typeWellToBeSelected &&
      !typeWellToBeSelected.isFolder &&
      !checkedTypeWellKeys.includes(typeWellToBeSelected.key)
    ) {
      const res: ICheckedForecast = {
        id: typeWellToBeSelected.key,
        type: "forecast",
        folderId: typeWellToBeSelected.folderId,
        children: typeWellToBeSelected.children,
        title: typeWellToBeSelected.title,
        reserveCategory: typeWellToBeSelected.reserveCategory,
        color: typeWellToBeSelected.color,
        thickness: typeWellToBeSelected.thickness,
        uniqueID: typeWellToBeSelected.uniqueID,
        arps: typeWellToBeSelected.arps,
        constants: typeWellToBeSelected.constants,
        isFolder: false,
        folderName: undefined,
        wellData: typeWellToBeSelected.wellData
      };

      setCheckedTypeWellKeys([...checkedTypeWellKeys, res.id]);
      setCheckedGlobalTypeWells([...checkedGlobalTypeWells, res]);
    }
  };

  const onTypeWellValueChange = (
    typeWell: UserArpsItem,
    _hasErrors?: boolean,
    _hasWarnings?: boolean,
    options?: { recordHistory: boolean }
  ) => {
    //When editing values the type well type will reset to undefined, so it must be manually set
    //eslint-disable-next-line
    const { isChecked, key, ...rest } = typeWell;
    const updatedTypeWell: ICheckedForecast = {
      ...rest,
      title: typeWell.title ?? selectedTypeWell.title,
      id: typeWell.key,
      type: typeWell.isFolder ? "folder" : "forecast",
      isFolder: false,
      folderName: undefined
    };
    const idx = checkedTypeWells.findIndex((f) => f.id === updatedTypeWell.id);
    const undoFcst = [...checkedTypeWells];
    const command = new Command(
      () => {
        const selectedForecastTypeWells = [...checkedTypeWells];
        if (idx >= 0) {
          selectedForecastTypeWells.splice(idx, 1, updatedTypeWell);
        } else {
          selectedForecastTypeWells.push(updatedTypeWell);
        }
        setIsTypeWellSavable(true);
        setHasUnsavedChanges(true);
        setCheckedGlobalTypeWells(selectedForecastTypeWells);
        setCheckedTypeWells(selectedForecastTypeWells);
        setCheckedTypeWellKeys(selectedForecastTypeWells.map((x) => x.id));
        setSelectedTypeWell({
          ...selectedTypeWell,
          title: typeWell.title
        });
        changeSelectedTypeWell(typeWell);
        updateTypeWellDataWithParentNode(typeWell);
      },
      () => {
        const oldVal = idx >= 0 ? undoFcst[idx] : null;
        setIsTypeWellSavable(true);
        setHasUnsavedChanges(true);
        setCheckedGlobalTypeWells(undoFcst);
        setCheckedTypeWells(undoFcst);
        setCheckedTypeWellKeys(undoFcst.map((x) => x.id));
        changeSelectedTypeWell(oldVal as UserArpsItem);
        setSelectedTypeWell({
          ...selectedTypeWell,
          title: oldVal.title
        });
        updateTypeWellDataWithParentNode(oldVal as UserArpsItem);
      }
    );
    if (options?.recordHistory === false) {
      command.execute();
    } else {
      undoRedoManager.executeCommand(command);
    }
  };

  return { onTypeWellValueChange, changeSelectedTypeWell };
}
